import { useEffect } from 'react';
import { getFocusableElement } from './getFocusableElement';

type UseFocusFirstElementOptions = {
  containerRef: React.RefObject<HTMLElement | null>;
  shouldFocus: boolean;
  excludeSelector?: string;
};

/**
 * Hook to focus the first focusable element within a container when conditions are met.
 *
 * Uses `getFocusableElement` to find the best element to focus, respecting the
 * `data-autofocus` attribute and optional exclude selector.
 *
 * @param options.containerRef - Ref to the container element to search for focusable elements
 * @param options.shouldFocus - When true, the first focusable element will receive focus
 * @param options.excludeSelector - Optional CSS selector for elements to exclude from focus search
 */
export const useFocusFirstElement = ({
  containerRef,
  shouldFocus,
  excludeSelector,
}: UseFocusFirstElementOptions): void => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !shouldFocus) return;

    getFocusableElement(container, excludeSelector).focus();
  }, [shouldFocus, excludeSelector, containerRef]);
};
